import type { BaseDocument } from "./data";

/**
 * What a VLM call was for, as the cost report splits it. Every `VlmPassType`
 * maps to one group (see `vlmCostGroup` in @sitescene/util); `Other` catches
 * a pass type the mapping doesn't know so its spend still shows.
 */
export enum VlmCostGroup {
	/** Worker second looks at flagged clips: collision, threat, fire, vandalism */
	FollowUp = "follow_up",
	Other = "other",
	/** Per-camera scene description, geometry and scene-fact drafts */
	Scene = "scene",
	/** Staff corrections re-running the evidence passes */
	StaffReanalysis = "staff_reanalysis",
	/** The first analysis of every clip, any pipeline */
	StageOne = "stage_one",
	/** Plausibility verification of alerts */
	Verification = "verification",
}

export enum VlmPassType {
	Collision = "collision",
	Fire = "fire",
	FirstPass = "first_pass",
	/**
	 * Plausibility verification passes. Like scene description these originate
	 * in TypeScript and bill to the same Gemini account, so they sit on the same
	 * cost report. `anchored` observes discriminating features on
	 * `PLAUSIBILITY_ANCHORED_MODEL`; `blind` re-identifies the clip on
	 * `PLAUSIBILITY_BLIND_MODEL`; `classify` is the canonicalization fallback
	 * when the alias table misses.
	 */
	PlausibilityAnchored = "plausibility_anchored",
	/** The anchored checklist re-run on the shadow model; recorded, never scored. */
	PlausibilityAnchoredShadow = "plausibility_anchored_shadow",
	PlausibilityBlind = "plausibility_blind",
	PlausibilityClassify = "plausibility_classify",
	/**
	 * The same constrained classifier pointed at a reviewer's own words in the
	 * verdict panel, mapping "that's not a bear, it's a dog" onto a class id.
	 * A separate pass type because it is human-triggered, one per click, and
	 * its accuracy is measured against the verdict the reviewer finally saved.
	 */
	PlausibilityVerdictClassify = "plausibility_verdict_classify",
	/**
	 * A staff reanalysis re-running the evidence passes against a staff
	 * member's correction. Separate from the pipeline's pass types so the cost
	 * report shows what staff corrections spend, and so none of it is mistaken
	 * for the verifier's own volume.
	 */
	ReanalysisAnchored = "reanalysis_anchored",
	ReanalysisBlind = "reanalysis_blind",
	/**
	 * Per-camera scene-description generation. Unlike every other pass this one
	 * originates in TypeScript rather than the Python worker, and runs against
	 * still frames on a schedule rather than against a clip on ingest — but it
	 * bills to the same Gemini account, so it belongs on the same cost report.
	 */
	SceneDescription = "scene_description",
	/**
	 * Drafting human-taught scene facts from a note about one clip. Staff- or
	 * customer-edit-triggered, one call (plus at most one directive retry) per
	 * proposal, and billed per sensor like the other TypeScript passes.
	 */
	SceneFactsDraft = "scene_facts_draft",
	/** Camera geometry paragraph, generated beside the scene description. */
	SceneGeometry = "scene_geometry",
	Threat = "threat",
	Vandalism = "vandalism",
}

export enum VlmThinkingLevel {
	High = "high",
	Low = "low",
	Medium = "medium",
	Minimal = "minimal",
}

/**
 * Raw row returned by VlmUsageRepository.getCostByRange — one per
 * (installationId, sensorId) pair with recorded usage in [from, to).
 */
export interface AggregatedRangeSensorCost {
	/** Every recorded generate_content call, priced or not */
	callCount: number;
	/** `totalCostUsd` by stored pass type */
	costByPassType: Record<string, number>;
	installationId: string;
	sensorId: null | string;
	totalCostUsd: number;
	/** Calls on a model missing from the pricing table (null cost) */
	unpricedCallCount: number;
	/** Unique videos (mediaKey) */
	videoCount: number;
}

/**
 * Raw row returned by VlmUsageRepository.getCostSummary — one per
 * (installationId, sensorId) pair with recorded usage in the window.
 * `costPerDay` is derived on the service side from `costLast30Days`.
 */
export interface AggregatedSensorCost {
	costLast30Days: number;
	costPerVideo: number;
	installationId: string;
	sensorId: null | string;
	videoCount: number;
}

/**
 * Raw row returned by VlmUsageRepository.getDurationBySensor — total analyzed
 * video seconds per (installationId, sensorId) over the window, deduped by
 * mediaKey so a multi-pass video counts its duration once.
 */
export interface AggregatedSensorDuration {
	installationId: string;
	sensorId: null | string;
	totalSeconds: number;
	videoCount: number;
}

/**
 * Average analyzed-video volume over the last 30 days, taken across the sensors
 * that had any measurable activity in the window (a sensor with zero analyzed
 * seconds is excluded from the average, not counted as a 0). Used by the eval
 * tool to project per-clip costs onto typical real production usage.
 */
export interface AverageSensorUsage {
	/** `secondsPerMonth` expressed in whole minutes. */
	minutesPerMonth: number;
	/** Average analyzed video seconds per active sensor over the 30-day window. */
	secondsPerMonth: number;
	/** Number of sensors with activity that the average was taken over. */
	sensorCount: number;
}

export type CreateVlmUsageParams = Omit<
	VlmUsage,
	"_cursor" | "createdAt" | "id" | "updatedAt"
>;

export interface InstallationUsageSummary {
	/** Human-facing installation ID (e.g. "piper-inn-001") */
	externalId: string;
	/** Internal installation UUID */
	id: string;
	sensors: SensorUsageSummary[];
}

/**
 * One calendar month (UTC) of VLM spend for a tenant, grouped by installation
 * then sensor. Sensor rows add up to the installation subtotal, and
 * installation subtotals to the month total: spend that can't be tied to a
 * known sensor shows as an "Unattributed" row rather than disappearing.
 */
export interface MonthlyCostSummary {
	callCount: number;
	/** `totalCostUsd` split by what the calls were for */
	costByGroup: VlmCostByGroup;
	/** Window start, ISO — the first instant of the month, UTC */
	from: string;
	installations: MonthlyInstallationCost[];
	/** "YYYY-MM" */
	month: string;
	/** Window end (exclusive), ISO — now for the current month */
	to: string;
	totalCostUsd: number;
	unpricedCallCount: number;
}

export interface MonthlyInstallationCost {
	callCount: number;
	costByGroup: VlmCostByGroup;
	/** Human-facing installation ID (e.g. "piper-inn-001") */
	externalId: string;
	/** Internal installation UUID */
	id: string;
	sensors: MonthlySensorCost[];
	totalCostUsd: number;
}

export interface MonthlySensorCost {
	callCount: number;
	costByGroup: VlmCostByGroup;
	/** Average cost per unique video (mediaKey), USD */
	costPerVideo: number;
	/** Human-facing sensor ID; null for the unattributed row */
	externalId: null | string;
	/** Internal sensor UUID; null for the unattributed row */
	id: null | string;
	/** User-set sensor name, or "Unattributed" */
	name: string;
	totalCostUsd: number;
	unpricedCallCount: number;
	videoCount: number;
}

export interface SensorUsageSummary {
	/** Total VLM cost over the last 30 days, USD */
	costLast30Days: number;
	/** Average daily cost over the last 30 days (total / 30), USD */
	costPerDay: number;
	/** Average cost per unique video (mediaKey) over the last 30 days, USD */
	costPerVideo: number;
	/** Human-facing sensor ID (e.g. "interior-pool-table") — matches VlmUsage.sensorId */
	externalId: string;
	/** Internal sensor UUID */
	id: string;
	/** User-set sensor name */
	name: string;
}

export interface TenantUsageSummary {
	installations: InstallationUsageSummary[];
}

export type VlmCostByGroup = Record<VlmCostGroup, number>;

export interface VlmUsage extends BaseDocument {
	/** Output tokens billed to Gemini (visible JSON only, not thinking) */
	candidatesTokens: number;
	/** "completed_at" timestamp from the VLM worker */
	completedAt: Date;
	/** Duration of the generate_content call in ms */
	durationMs: number;
	/** Error code when success is false (e.g., "429") */
	errorCode?: string;
	/** Error message when success is false */
	errorMessage?: string;
	/** Input (prompt) cost in USD. Null when model is not in the pricing table. */
	inputCostUsd: null | number;
	/** External installation ID from the source event */
	installationId: string;
	/** S3 media key the analysis was run against */
	mediaKey: string;
	/** Gemini model name, e.g. "gemini-3.5-flash-lite" */
	model: string;
	/** Output (candidates + thoughts) cost in USD. Null when model is unknown. */
	outputCostUsd: null | number;
	/** Which generate_content call site this record corresponds to */
	passType: VlmPassType;
	/**
	 * Pipeline the pass ran in (e.g. "anomaly", "bolo"), split off the worker's
	 * suffixed pass type ("first_pass:bolo"). Absent for unsuffixed passes.
	 */
	pipeline?: string;
	/** Pricing table version (e.g. "2026-04"). Bump when GEMINI_PRICING changes. */
	pricingVersion: string;
	/** Input tokens reported by usage_metadata.prompt_token_count */
	promptTokens: number;
	/**
	 * Per-modality breakdown of the prompt tokens, e.g. { VIDEO: 18000, TEXT: 500 }.
	 * Derived from usage_metadata.prompt_tokens_details.
	 */
	promptTokensByModality: Record<string, number>;
	/** External sensor ID */
	sensorId?: string;
	/** "started_at" timestamp from the VLM worker */
	startedAt: Date;
	/** Whether the generate_content call succeeded */
	success: boolean;
	/** Thinking level used for this call */
	thinkingLevel: VlmThinkingLevel;
	/** Reasoning tokens reported by usage_metadata.thoughts_token_count (Gemini bills as output) */
	thoughtsTokens: number;
	/** Total cost in USD (input + output). Null when model is unknown. */
	totalCostUsd: null | number;
	/** Total tokens reported by usage_metadata.total_token_count */
	totalTokens: number;
	/** Video duration in seconds (when available) */
	videoDurationSeconds?: number;
	/** External zone ID */
	zoneId?: string;
}

/**
 * EventBridge detail payload for a `vlm.usage` event.
 * Emitted by the Python VLM worker (packages/ai-vlm/worker.py:send_usage_event)
 * after every generate_content call (success or failure). Field names are
 * camelCase to match the TS side directly.
 */
export interface VlmUsageEventDetail {
	candidatesTokens: number;
	completedAt: string;
	durationMs: number;
	errorCode?: string;
	errorMessage?: string;
	inputCostUsd: null | number;
	installationId: string;
	mediaKey: string;
	model: string;
	outputCostUsd: null | number;
	/**
	 * A `VlmPassType`, optionally suffixed with the pipeline type — the worker
	 * sends "first_pass:anomaly" / "first_pass:bolo" for pipeline first passes.
	 */
	passType: string;
	pricingVersion: string;
	promptTokens: number;
	promptTokensByModality?: Record<string, number>;
	sensorId?: string;
	startedAt: string;
	success: boolean;
	thinkingLevel: VlmThinkingLevel;
	thoughtsTokens: number;
	totalCostUsd: null | number;
	totalTokens: number;
	videoDurationSeconds?: number;
	zoneId?: string;
}

/** Wrapper for EventBridge messages received via SQS */
export interface VlmUsageEventMessage {
	detail: VlmUsageEventDetail;
	"detail-type": string;
	source: string;
}
